import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { StarIcon as StarOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import apiClient from '../../services/apiClient';

interface CourseReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: number;
  courseName?: string;
  /** Gọi sau khi gửi/cập nhật thành công (trước khi đóng). */
  onSaved?: () => void;
}

const reviewFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'align',
  'link',
  'image',
  'code-block',
];

const isEmptyHtml = (value: string) => {
  const stripped = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return stripped === '';
};

const dataUrlToFile = async (dataUrl: string, filename: string): Promise<File> => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

const uploadImageFile = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/student/uploads/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  if (!response.data?.success || !response.data?.data?.url) {
    throw new Error(response.data?.message || 'Upload ảnh thất bại');
  }
  return response.data.data.url as string;
};

const replaceBase64ImagesInHtml = async (html: string): Promise<string> => {
  if (!html || !html.includes('data:image')) return html;
  const regex = /src=(["'])(data:image\/[a-zA-Z0-9+.-]+;base64,[^"']+)\1/gi;
  const matches = [...html.matchAll(regex)];
  if (!matches.length) return html;

  let next = html;
  for (let i = 0; i < matches.length; i += 1) {
    const dataUrl = matches[i][2];
    try {
      const file = await dataUrlToFile(dataUrl, `review-${Date.now()}-${i}.png`);
      const url = await uploadImageFile(file);
      next = next.split(dataUrl).join(url);
    } catch (err) {
      console.error('Failed to upload pasted image', err);
    }
  }
  return next;
};

const CourseReviewModal: React.FC<CourseReviewModalProps> = ({
  isOpen,
  onClose,
  courseId,
  courseName,
  onSaved,
}) => {
  const [rate, setRate] = useState(0);
  const [hoverRate, setHoverRate] = useState(0);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [existingId, setExistingId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const loadReview = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get(`/student/courses/${courseId}/review`);
      const data = res.data?.data;
      if (data) {
        setExistingId(data.id);
        setRate(data.rate || 0);
        setNote(data.note || '');
      } else {
        setExistingId(null);
        setRate(0);
        setNote('');
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || 'Không tải được đánh giá hiện có');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (isOpen) {
      loadReview();
    } else {
      setHoverRate(0);
      setError('');
    }
  }, [isOpen, loadReview]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image() {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();
            input.onchange = async () => {
              const file = input.files?.[0];
              if (!file) return;
              const quill = (this as any).quill;
              const range = quill?.getSelection(true);
              const insertAt = range?.index ?? quill?.getLength?.() ?? 0;
              try {
                setUploadingImage(true);
                const url = await uploadImageFile(file);
                quill.insertEmbed(insertAt, 'image', url, 'user');
                quill.setSelection(insertAt + 1, 0, 'silent');
              } catch (err: any) {
                console.error(err);
                toast.error(err?.response?.data?.message || err?.message || 'Upload ảnh thất bại');
              } finally {
                setUploadingImage(false);
              }
            };
          },
        },
      },
    }),
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (rate < 1 || rate > 5) {
      setError('Vui lòng chọn số sao đánh giá');
      return;
    }
    if (isEmptyHtml(note)) {
      setError('Vui lòng nhập nội dung đánh giá');
      return;
    }

    setSubmitting(true);
    try {
      const cleanedNote = await replaceBase64ImagesInHtml(note);
      const res = await apiClient.post(`/student/courses/${courseId}/review`, {
        rate,
        note: cleanedNote,
      });
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Gửi đánh giá thất bại');
      }
      toast.success(res.data?.message || 'Đã gửi đánh giá khóa học');
      onSaved?.();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Gửi đánh giá thất bại';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const displayRate = hoverRate || rate;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[80]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-eh bg-eh-cream text-eh-charcoal shadow-2xl ring-1 ring-eh-border transition-all">
                <div className="flex items-center justify-between border-b border-eh-border px-5 py-4 bg-eh-keylime/60">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-slate-900">
                      {existingId ? 'Đánh giá của tôi' : 'Đánh giá khóa học'}
                    </Dialog.Title>
                    {courseName ? (
                      <p className="mt-0.5 text-sm text-slate-600 line-clamp-1">{courseName}</p>
                    ) : null}
                    {existingId ? (
                      <p className="mt-1 text-xs text-slate-500">
                        Bạn có thể xem lại và cập nhật đánh giá nếu muốn.
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                    aria-label="Đóng"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-16 text-slate-600">
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Đang tải...
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="px-5 py-4 space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-800 mb-2">
                        Số sao <span className="text-red-600">*</span>
                      </label>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const active = star <= displayRate;
                          const Icon = active ? StarSolid : StarOutline;
                          return (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setRate(star)}
                              onMouseEnter={() => setHoverRate(star)}
                              onMouseLeave={() => setHoverRate(0)}
                              className="p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
                              aria-label={`${star} sao`}
                            >
                              <Icon
                                className={`h-8 w-8 transition-colors ${
                                  active ? 'text-amber-400' : 'text-slate-300 hover:text-amber-300'
                                }`}
                              />
                            </button>
                          );
                        })}
                        {rate > 0 ? (
                          <span className="ml-2 text-sm font-medium text-slate-600">{rate}/5</span>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-800 mb-2">
                        Nội dung đánh giá <span className="text-red-600">*</span>
                      </label>
                      <p className="mb-2 text-xs text-slate-500">
                        Có thể định dạng văn bản và chèn ảnh (nút ảnh trên thanh công cụ).
                      </p>
                      <div className="relative rounded-lg border border-slate-300 overflow-hidden bg-white shadow-sm">
                        <ReactQuill
                          theme="snow"
                          value={note}
                          onChange={setNote}
                          modules={modules}
                          formats={reviewFormats}
                          placeholder="Chia sẻ cảm nhận của bạn về khóa học..."
                          className="course-review-quill rounded-lg overflow-hidden"
                        />
                        {uploadingImage ? (
                          <div className="absolute inset-0 bg-white/80 flex items-center justify-center text-sm text-slate-700 z-10">
                            <i className="fas fa-spinner fa-spin mr-2" />
                            Đang tải ảnh lên...
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {error ? (
                      <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                        {error}
                      </div>
                    ) : null}

                    <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
                      <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                        disabled={submitting}
                      >
                        Hủy
                      </button>
                      <button
                        type="submit"
                        disabled={submitting || uploadingImage}
                        className="rounded-eh-sm bg-eh-forest-ink px-5 py-2 text-sm font-semibold text-eh-cream hover:bg-eh-forest-shadow disabled:opacity-60"
                      >
                        {submitting
                          ? 'Đang gửi...'
                          : existingId
                            ? 'Cập nhật đánh giá'
                            : 'Gửi đánh giá'}
                      </button>
                    </div>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CourseReviewModal;
